import { useState } from "react";
import { Star, MapPin, BadgeCheck, Heart, ArrowRight, Search, MessageSquare } from "lucide-react";

/*
  NCONTRO — Light Visual Modernization (Preview only)
  70% modern (Airbnb/OpenTable clarity) • 30% elegant (editorial)
  All tokens below are scoped to .preview-scope so the current app is untouched.
*/

const scopeStyles = `
  .preview-scope {
    /* Font families */
    --pv-serif: 'Fraunces', 'Cormorant Garamond', Georgia, serif;
    --pv-sans: 'Inter', ui-sans-serif, system-ui, sans-serif;

    /* Palette */
    --pv-bg: #FAFAF7;
    --pv-surface: #F4F1EA;
    --pv-surface-2: #ECE7DD;
    --pv-text: #1F1F1F;
    --pv-text-muted: #6B6B6B;
    --pv-border: #E5E1D8;
    --pv-border-strong: #D4CFC3;

    /* Accent — warm terracotta / event-planning coral */
    --pv-accent: #D9583A;
    --pv-accent-hover: #B84730;
    --pv-accent-soft: #FBEAE4;

    /* Supporting */
    --pv-verified: #2F7D4F;
    --pv-verified-soft: #E4F1E8;
    --pv-star: #F5A524;

    font-family: var(--pv-sans);
    background: var(--pv-bg);
    color: var(--pv-text);
  }
  .preview-scope * { border-color: var(--pv-border); }
  .preview-scope .display { font-family: var(--pv-serif); font-weight: 400; letter-spacing: -0.02em; }
  .preview-scope .eyebrow-new { font-size: 0.7rem; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 600; color: var(--pv-text-muted); }

  /* Buttons */
  .preview-scope .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; font-weight: 600; font-size: 0.9rem; padding: 0 1.25rem; height: 44px; border-radius: 10px; transition: background-color 180ms, color 180ms, border-color 180ms, transform 180ms; }
  .preview-scope .btn-primary { background: var(--pv-accent); color: #fff; }
  .preview-scope .btn-primary:hover { background: var(--pv-accent-hover); }
  .preview-scope .btn-secondary { background: #fff; color: var(--pv-text); border: 1px solid var(--pv-border-strong); }
  .preview-scope .btn-secondary:hover { background: var(--pv-surface); }
  .preview-scope .btn-ghost { background: transparent; color: var(--pv-text); }
  .preview-scope .btn-ghost:hover { background: var(--pv-surface); }
  .preview-scope .btn-lg { height: 52px; font-size: 0.95rem; padding: 0 1.5rem; }

  /* Input */
  .preview-scope .input { width: 100%; height: 48px; padding: 0 14px; border: 1px solid var(--pv-border-strong); border-radius: 10px; background: #fff; font-size: 0.95rem; transition: border-color 160ms, box-shadow 160ms; }
  .preview-scope .input:focus { outline: none; border-color: var(--pv-accent); box-shadow: 0 0 0 3px var(--pv-accent-soft); }

  /* Search pill */
  .preview-scope .search-pill { display: flex; align-items: center; background: #fff; border: 1px solid var(--pv-border-strong); border-radius: 999px; box-shadow: 0 4px 16px rgba(0,0,0,0.04); overflow: hidden; }
  .preview-scope .search-pill .cell { padding: 12px 20px; }
  .preview-scope .search-pill .divider { width: 1px; height: 28px; background: var(--pv-border); }

  /* Vendor card */
  .preview-scope .v-card { background: #fff; border: 1px solid var(--pv-border); border-radius: 16px; overflow: hidden; transition: transform 260ms, box-shadow 260ms; }
  .preview-scope .v-card:hover { transform: translateY(-4px); box-shadow: 0 24px 40px rgba(0,0,0,0.08); }
  .preview-scope .badge { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 999px; font-size: 0.7rem; font-weight: 600; }
  .preview-scope .badge-verified { background: var(--pv-verified-soft); color: var(--pv-verified); }
  .preview-scope .badge-featured { background: #fff; color: var(--pv-text); border: 1px solid var(--pv-border-strong); }

  /* Chip */
  .preview-scope .chip-new { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 999px; border: 1px solid var(--pv-border-strong); background: #fff; font-size: 0.85rem; color: var(--pv-text); cursor: pointer; transition: background-color 160ms, border-color 160ms; }
  .preview-scope .chip-new:hover { background: var(--pv-surface); }
  .preview-scope .chip-new.active { background: var(--pv-text); color: #fff; border-color: var(--pv-text); }
`;

export default function DesignPreview() {
  const [fav, setFav] = useState(false);

  return (
    <div className="preview-scope min-h-screen">
      <style>{scopeStyles}</style>
      {/* Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div className="max-w-[1200px] mx-auto px-6 py-16">
        <div className="eyebrow-new mb-3">Design preview — light modernization</div>
        <h1 className="display text-5xl sm:text-6xl mb-3">NContro · Refresh</h1>
        <p className="text-[color:var(--pv-text-muted)] max-w-2xl leading-relaxed">
          70% modern marketplace clarity (Airbnb/OpenTable) · 30% editorial warmth. Approve below and I'll roll the tokens across the app.
        </p>

        {/* PALETTE */}
        <section className="mt-16">
          <div className="eyebrow-new mb-4">01 — Color palette</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
            <Swatch color="#FAFAF7" name="Background" hex="#FAFAF7" light />
            <Swatch color="#F4F1EA" name="Surface" hex="#F4F1EA" light />
            <Swatch color="#1F1F1F" name="Text" hex="#1F1F1F" />
            <Swatch color="#6B6B6B" name="Muted" hex="#6B6B6B" />
            <Swatch color="#D9583A" name="Accent" hex="#D9583A" star />
            <Swatch color="#2F7D4F" name="Verified" hex="#2F7D4F" />
            <Swatch color="#E5E1D8" name="Border" hex="#E5E1D8" light />
          </div>
          <p className="text-sm text-[color:var(--pv-text-muted)] mt-4 max-w-2xl leading-relaxed">
            Warm off-white background, deep charcoal text, one confident terracotta accent that feels appetizing and event-forward without being neon. Verified green is only used for the trust badge on vendors.
          </p>
        </section>

        {/* TYPOGRAPHY */}
        <section className="mt-16">
          <div className="eyebrow-new mb-4">02 — Typography</div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl border" style={{background:"#fff"}}>
              <div className="eyebrow-new mb-3">Display — Fraunces (editorial, large headings only)</div>
              <div className="display text-6xl leading-none">Compose unforgettable events</div>
              <div className="display text-3xl mt-4">The NContro collective</div>
            </div>
            <div className="p-8 rounded-2xl border" style={{background:"#fff"}}>
              <div className="eyebrow-new mb-3">UI — Inter (all body, nav, buttons, prices, dashboards)</div>
              <div className="text-2xl font-semibold">Maison Verre Catering</div>
              <div className="text-sm text-[color:var(--pv-text-muted)] mt-1">Refined French tasting menus · New York</div>
              <div className="text-base mt-4 leading-relaxed">
                An award-winning kitchen crafting seasonal tasting menus with impeccable service. Booked by planners for weddings, galas and intimate dinners.
              </div>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="text-2xl font-semibold">$165</span>
                <span className="text-sm text-[color:var(--pv-text-muted)]">/ guest</span>
              </div>
            </div>
          </div>
        </section>

        {/* BUTTONS */}
        <section className="mt-16">
          <div className="eyebrow-new mb-4">03 — Buttons</div>
          <div className="p-8 rounded-2xl border flex flex-wrap gap-3 items-center" style={{background:"#fff"}}>
            <button className="btn btn-primary btn-lg">Request a quote <ArrowRight className="w-4 h-4" /></button>
            <button className="btn btn-primary">Browse vendors</button>
            <button className="btn btn-secondary">View profile</button>
            <button className="btn btn-ghost">Learn more</button>
            <button className="btn btn-primary" disabled style={{opacity:0.5, cursor:"not-allowed"}}>Disabled</button>
          </div>
        </section>

        {/* FORM + SEARCH */}
        <section className="mt-16">
          <div className="eyebrow-new mb-4">04 — Forms & search</div>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl border" style={{background:"#fff"}}>
              <label className="text-sm font-medium">Email</label>
              <input className="input mt-2" placeholder="you@company.com" />
              <label className="text-sm font-medium mt-4 block">City</label>
              <input className="input mt-2" placeholder="e.g. New York" />
            </div>
            <div className="p-8 rounded-2xl border flex flex-col justify-center" style={{background:"#fff"}}>
              <div className="text-sm font-medium mb-3">Marketplace search bar (Airbnb-style)</div>
              <div className="search-pill">
                <div className="cell flex-1">
                  <div className="text-[0.7rem] font-semibold uppercase tracking-wider text-[color:var(--pv-text-muted)]">Category</div>
                  <div className="text-sm">Catering</div>
                </div>
                <div className="divider" />
                <div className="cell flex-1">
                  <div className="text-[0.7rem] font-semibold uppercase tracking-wider text-[color:var(--pv-text-muted)]">Location</div>
                  <div className="text-sm">New York</div>
                </div>
                <div className="divider" />
                <div className="cell flex-1">
                  <div className="text-[0.7rem] font-semibold uppercase tracking-wider text-[color:var(--pv-text-muted)]">Date</div>
                  <div className="text-sm text-[color:var(--pv-text-muted)]">Add date</div>
                </div>
                <div className="cell">
                  <button className="btn btn-primary" style={{height:44, width:44, padding:0, borderRadius:999}}>
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-5">
                <span className="chip-new active">All</span>
                <span className="chip-new">Catering</span>
                <span className="chip-new">Venues</span>
                <span className="chip-new">Florists</span>
                <span className="chip-new">DJs</span>
              </div>
            </div>
          </div>
        </section>

        {/* VENDOR CARD */}
        <section className="mt-16">
          <div className="eyebrow-new mb-4">05 — Vendor card sample</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="v-card">
              <div className="relative aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1467003909585-2f8a72700288?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwY2F0ZXJpbmclMjBmb29kJTIwcGxhdGluZ3xlbnwwfHx8fDE3ODUzNDYxNjh8MA&ixlib=rb-4.1.0&q=85"
                  alt="Maison Verre Catering"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 left-3 flex gap-2">
                  <span className="badge badge-featured">Featured</span>
                </div>
                <button
                  onClick={() => setFav(!fav)}
                  aria-label="Save vendor"
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
                >
                  <Heart className={`w-4 h-4 ${fav ? "fill-[#D9583A] text-[#D9583A]" : "text-[#1F1F1F]"}`} />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold truncate">Maison Verre Catering</h3>
                      <span className="badge badge-verified">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-[color:var(--pv-text-muted)] mt-1">
                      <MapPin className="w-3.5 h-3.5" /> New York · Catering
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Star className="w-4 h-4 fill-[#F5A524] text-[#F5A524]" />
                    <span className="text-sm font-semibold">4.9</span>
                    <span className="text-xs text-[color:var(--pv-text-muted)]">(128)</span>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-xs text-[color:var(--pv-text-muted)]">from</span>
                  <span className="text-xl font-semibold">$165</span>
                  <span className="text-xs text-[color:var(--pv-text-muted)]">/ guest</span>
                </div>
                <div className="mt-5 flex gap-2">
                  <button className="btn btn-secondary flex-1">View profile</button>
                  <button className="btn btn-primary flex-1">
                    <MessageSquare className="w-4 h-4" /> Request quote
                  </button>
                </div>
              </div>
            </div>

            {/* Second card (denser variant, no featured badge) */}
            <div className="v-card">
              <div className="relative aspect-[4/3]">
                <img
                  src="https://images.pexels.com/photos/12954015/pexels-photo-12954015.jpeg"
                  alt="The Ashcroft Estate"
                  className="w-full h-full object-cover"
                />
                <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
                  <Heart className="w-4 h-4 text-[#1F1F1F]" />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-semibold truncate">The Ashcroft Estate</h3>
                      <span className="badge badge-verified"><BadgeCheck className="w-3 h-3" /> Verified</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-[color:var(--pv-text-muted)] mt-1">
                      <MapPin className="w-3.5 h-3.5" /> Hudson Valley · Venue
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Star className="w-4 h-4 fill-[#F5A524] text-[#F5A524]" />
                    <span className="text-sm font-semibold">4.8</span>
                    <span className="text-xs text-[color:var(--pv-text-muted)]">(64)</span>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-xs text-[color:var(--pv-text-muted)]">from</span>
                  <span className="text-xl font-semibold">$12,000</span>
                  <span className="text-xs text-[color:var(--pv-text-muted)]">/ event</span>
                </div>
                <div className="mt-5 flex gap-2">
                  <button className="btn btn-secondary flex-1">View profile</button>
                  <button className="btn btn-primary flex-1">
                    <MessageSquare className="w-4 h-4" /> Request quote
                  </button>
                </div>
              </div>
            </div>

            {/* Third card (florist) */}
            <div className="v-card">
              <div className="relative aspect-[4/3]">
                <img
                  src="https://images.unsplash.com/photo-1561593367-66c79c2294e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxldmVudCUyMGZsb3Jpc3QlMjBmbG9yYWwlMjBhcnJhbmdlbWVudCUyMHRhYmxlfGVufDB8fHx8MTc4NTM0NjE2OHww&ixlib=rb-4.1.0&q=85"
                  alt="Fleur & Feuille"
                  className="w-full h-full object-cover"
                />
                <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center">
                  <Heart className="w-4 h-4 fill-[#D9583A] text-[#D9583A]" />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold truncate">Fleur &amp; Feuille</h3>
                    <div className="flex items-center gap-1 text-sm text-[color:var(--pv-text-muted)] mt-1">
                      <MapPin className="w-3.5 h-3.5" /> Los Angeles · Florist
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Star className="w-4 h-4 fill-[#F5A524] text-[#F5A524]" />
                    <span className="text-sm font-semibold">4.7</span>
                    <span className="text-xs text-[color:var(--pv-text-muted)]">(42)</span>
                  </div>
                </div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="text-xs text-[color:var(--pv-text-muted)]">from</span>
                  <span className="text-xl font-semibold">$2,400</span>
                  <span className="text-xs text-[color:var(--pv-text-muted)]">/ event</span>
                </div>
                <div className="mt-5 flex gap-2">
                  <button className="btn btn-secondary flex-1">View profile</button>
                  <button className="btn btn-primary flex-1">
                    <MessageSquare className="w-4 h-4" /> Request quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category tiles */}
        <section className="mt-16">
          <div className="eyebrow-new mb-4">06 — Category tiles</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {["Catering", "Restaurants", "Venues", "Florists", "DJs"].map((c) => (
              <div key={c} className="v-card p-4 text-center cursor-pointer">
                <div className="w-12 h-12 rounded-full bg-[color:var(--pv-surface)] mx-auto flex items-center justify-center">
                  <span className="text-lg display">{c[0]}</span>
                </div>
                <div className="mt-3 text-sm font-semibold">{c}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 p-8 rounded-2xl border" style={{background:"var(--pv-surface)"}}>
          <div className="eyebrow-new mb-2">Ready?</div>
          <div className="display text-3xl mb-3">If this direction looks right, reply "apply" and I'll roll it across the app.</div>
          <div className="text-sm text-[color:var(--pv-text-muted)] leading-relaxed max-w-2xl">
            Scope of the roll-out: <code className="text-[color:var(--pv-text)] px-1 py-0.5 rounded bg-white">index.css</code> tokens & fonts, <code className="text-[color:var(--pv-text)] px-1 py-0.5 rounded bg-white">Header</code> / <code className="text-[color:var(--pv-text)] px-1 py-0.5 rounded bg-white">Footer</code>, vendor cards on Landing & Browse, category tiles, buttons, form inputs, and hover/badge/spacing consistency. Backend, routes and dashboards remain untouched.
          </div>
        </section>
      </div>
    </div>
  );
}

function Swatch({ color, name, hex, light, star }) {
  return (
    <div>
      <div
        className="rounded-xl h-24 border relative"
        style={{ background: color, borderColor: light ? "#E5E1D8" : "transparent" }}
      >
        {star && (
          <span className="absolute bottom-2 right-2 text-white text-[10px] uppercase tracking-widest font-semibold">accent</span>
        )}
      </div>
      <div className="mt-2 text-sm font-medium">{name}</div>
      <div className="text-xs text-[color:var(--pv-text-muted)]">{hex}</div>
    </div>
  );
}
